
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sun, RefreshCw, Clock, AlertCircle, Target } from 'lucide-react';
import { useBatchSunStatus } from '@/hooks/useSunStatus';
import { useAdminTerraces } from '@/hooks/useAdminTerraces';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SunStatusManager = () => {
  const { triggerBatchCalculation, isProcessing, lastBatchResult, error } = useBatchSunStatus();
  const { terraces, loading: terracesLoading } = useAdminTerraces();
  const [selectedTerraceId, setSelectedTerraceId] = useState<string>('');
  const [isUpdatingSpecific, setIsUpdatingSpecific] = useState(false);
  const [specificError, setSpecificError] = useState<string | null>(null);

  const handleBatchUpdate = async () => {
    await triggerBatchCalculation();
    
    if (!error) {
      toast.success('Sun status calculation completed successfully');
    } else {
      toast.error('Failed to calculate sun status');
    }
  };

  const handleSpecificUpdate = async () => {
    if (!selectedTerraceId) {
      toast.error('Please select a terrace first');
      return;
    }

    setIsUpdatingSpecific(true);
    setSpecificError(null);

    try {
      const { data, error } = await supabase.functions.invoke('calculate-sun-status', {
        body: { terraceId: selectedTerraceId }
      });

      if (error) {
        throw error;
      }

      toast.success(`Sun status updated successfully for selected terrace`);
    } catch (error) {
      console.error('Error updating specific terrace sun status:', error);
      setSpecificError('Failed to update sun status for selected terrace');
      toast.error('Failed to update sun status');
    } finally {
      setIsUpdatingSpecific(false);
    }
  };

  const selectedTerrace = terraces.find(t => t.id === selectedTerraceId);

  return (
    <div className="space-y-6">
      <Card className="border-peach-orange/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sun className="h-5 w-5 text-sunset-yellow" />
            Sun Status Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Batch Update Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Batch Sun Status Calculation</h3>
                <p className="text-sm text-muted-foreground">
                  Update sun status for all terraces based on current time and orientation
                </p>
              </div>
              <Button 
                onClick={handleBatchUpdate}
                disabled={isProcessing}
                className="bg-peach-orange hover:bg-coral-pink"
              >
                {isProcessing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Sun className="h-4 w-4 mr-2" />
                    Update All
                  </>
                )}
              </Button>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-coral-pink/10 border border-coral-pink/30 rounded-md">
                <AlertCircle className="h-4 w-4 text-coral-pink" />
                <span className="text-sm text-coral-pink">{error}</span>
              </div>
            )}

            {lastBatchResult && (
              <div className="space-y-3 p-3 bg-teal-blue/10 border border-teal-blue/30 rounded-md">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-teal-blue" />
                  <span className="text-sm font-medium text-teal-blue">
                    Last Update: {new Date(lastBatchResult.calculatedAt).toLocaleString()}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Processed:</span>
                    <Badge variant="secondary" className="ml-2">
                      {lastBatchResult.processed} terraces
                    </Badge>
                  </div>
                  <div>
                    <span className="font-medium">Status:</span>
                    <Badge variant="outline" className="ml-2 text-teal-blue border-teal-blue/30">
                      {lastBatchResult.message}
                    </Badge>
                  </div>
                </div>

                {lastBatchResult.results && lastBatchResult.results.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium text-teal-blue">Recent Updates:</span>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {lastBatchResult.results.map((result: any, index: number) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <span className="truncate">{result.name}</span>
                          <Badge 
                            variant={result.status === 'sunny' ? 'default' : 'secondary'}
                            className="ml-2"
                          >
                            {result.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Separator */}
          <div className="border-t border-peach-orange/20 pt-4">
            {/* Specific Terrace Update Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Update Specific Terrace
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Calculate sun status for a specific terrace
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">
                    Select Terrace
                  </label>
                  <Select
                    value={selectedTerraceId}
                    onValueChange={setSelectedTerraceId}
                    disabled={terracesLoading}
                  >
                    <SelectTrigger className="border-peach-orange/30">
                      <SelectValue placeholder={terracesLoading ? "Loading..." : "Choose a terrace"} />
                    </SelectTrigger>
                    <SelectContent>
                      {terraces.map((terrace) => (
                        <SelectItem key={terrace.id} value={terrace.id}>
                          <div className="flex items-center justify-between w-full">
                            <span className="truncate">{terrace.name}</span>
                            <Badge 
                              variant={terrace.sunStatus === 'sunny' ? 'default' : 'secondary'}
                              className="ml-2 shrink-0"
                            >
                              {terrace.sunStatus}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  onClick={handleSpecificUpdate}
                  disabled={!selectedTerraceId || isUpdatingSpecific}
                  className="bg-peach-orange hover:bg-coral-pink"
                >
                  {isUpdatingSpecific ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Sun className="h-4 w-4 mr-2" />
                      Update
                    </>
                  )}
                </Button>
              </div>

              {selectedTerrace && (
                <div className="p-3 bg-warm-cream border border-peach-orange/20 rounded-md">
                  <div className="text-sm">
                    <span className="font-medium">Selected:</span> {selectedTerrace.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {selectedTerrace.address}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm">Current status:</span>
                    <Badge variant={selectedTerrace.sunStatus === 'sunny' ? 'default' : 'secondary'}>
                      {selectedTerrace.sunStatus}
                    </Badge>
                  </div>
                </div>
              )}

              {specificError && (
                <div className="flex items-center gap-2 p-3 bg-coral-pink/10 border border-coral-pink/30 rounded-md">
                  <AlertCircle className="h-4 w-4 text-coral-pink" />
                  <span className="text-sm text-coral-pink">{specificError}</span>
                </div>
              )}
            </div>
          </div>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>• Sun status is automatically updated every 15 minutes for active terraces</p>
            <p>• Manual updates can be triggered for immediate refresh</p>
            <p>• Calculations consider terrace orientation, time of day, and sun position</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SunStatusManager;
